if (!customElements.get('quick-add-hybrid')) {
  // Singleton cart state manager
  class HybridCartManager {
    constructor() {
      this.cache = null;
      this.fetchPromise = null;
      this.subscribers = new Set();
      this.initialized = false;
      
      // Single subscription to cart updates
      if (!this.initialized) {
        this.initialized = true;
        subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
      }
    }
    
    async fetchCart(force = false) {
      // Return cached if available and not forcing
      if (!force && this.cache) {
        return this.cache;
      }
      
      // If already fetching, return the existing promise to prevent parallel requests
      if (this.fetchPromise) {
        return this.fetchPromise;
      }
      
      // Create a single fetch promise that all components will share
      this.fetchPromise = fetch('/cart.js', { credentials: 'same-origin' })
        .then(res => res.json())
        .then(cart => {
          this.cache = cart;
          this.fetchPromise = null; // Clear the promise after completion
          return cart;
        })
        .catch(err => {
          this.fetchPromise = null; // Clear on error too
          throw err;
        });
      
      return this.fetchPromise;
    }
    
    handleCartUpdate(payload = {}) {
      // Extract cart data from various payload formats
      const cart = 
        payload.cartData && Array.isArray(payload.cartData.items) ? payload.cartData :
        payload.cart && Array.isArray(payload.cart.items) ? payload.cart :
        payload.state && Array.isArray(payload.state.items) ? payload.state :
        null;
      
      if (cart) {
        // Update cache and notify all components
        this.cache = cart;
        this.notifySubscribers(cart);
      } else {
        // Fetch once for all components
        this.fetchCart(true).then(freshCart => {
          this.notifySubscribers(freshCart);
        });
      }
    }
    
    subscribe(component) {
      this.subscribers.add(component);
      
      // Return unsubscribe function
      return () => {
        this.subscribers.delete(component);
      };
    }
    
    notifySubscribers(cart) {
      this.subscribers.forEach(component => {
        if (component.updateFromCart) {
          component.updateFromCart(cart);
        }
      });
    }
    
    async getInitialCart() {
      // Use shared fetch for initial load
      return this.fetchCart();
    }
    
    // NEW: Update cart bubble optimistically
    updateCartBubbleOptimistically(increment = 1) {
      // Find all cart icon wrappers (where bubbles should be)
      const cartIconWrappers = document.querySelectorAll('.header__icon-wrapper--cart');
      
      cartIconWrappers.forEach(wrapper => {
        let bubble = wrapper.querySelector('.cart-count-bubble');
        
        // If no bubble exists (cart was empty), create it
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          bubble.innerHTML = `
            <span aria-hidden="true">0</span>
            <span class="visually-hidden">0 items</span>
          `;
          
          // Find the icon SVG wrapper and insert bubble after it
          const iconSvg = wrapper.querySelector('.header__icon-svg');
          if (iconSvg) {
            iconSvg.appendChild(bubble);
          }
        }
        
        // Get current count
        const currentCountElement = bubble.querySelector('span[aria-hidden="true"]');
        if (!currentCountElement) return;
        
        const currentText = currentCountElement.textContent.trim();
        let currentCount = 0;
        
        if (currentText === '99+') {
          currentCount = 99;
        } else {
          currentCount = parseInt(currentText, 10) || 0;
        }
        
        // Update with new count
        const newCount = currentCount + increment;
        
        if (newCount > 99) {
          currentCountElement.textContent = '99+';
          bubble.classList.add('cart-count-bubble--extended');
        } else if (newCount > 0) {
          currentCountElement.textContent = newCount.toString();
          if (newCount < 100) {
            bubble.classList.remove('cart-count-bubble--extended');
          }
        }
        
        // Update aria label
        const ariaElement = bubble.querySelector('.visually-hidden');
        if (ariaElement) {
          ariaElement.textContent = `${newCount} ${newCount === 1 ? 'item' : 'items'}`;
        }
        
        // Ensure bubble is visible
        bubble.style.display = '';
        
        // Update empty cart icon to filled cart icon
        const svgWrapper = wrapper.querySelector('.svg-wrapper');
        if (svgWrapper && svgWrapper.innerHTML.includes('icon-cart-empty')) {
          svgWrapper.innerHTML = svgWrapper.innerHTML.replace('icon-cart-empty', 'icon-cart');
        }
      });
    }
  }
  
  // Create singleton instance
  window.__hybridCartManager = window.__hybridCartManager || new HybridCartManager();
  
  customElements.define(
    'quick-add-hybrid',
    class QuickAddHybrid extends HTMLElement {
      connectedCallback() {
        this.standardEl = this.querySelector('.hybrid__standard');
        this.bulkEl = this.querySelector('.hybrid__bulk');
        this.productId = parseInt(this.dataset.productId, 10);
        this.variantId = parseInt(this.dataset.variantId, 10);
        
        // Subscribe to the manager instead of directly to events
        this.unsubscribe = window.__hybridCartManager.subscribe(this);
        
        // Get initial cart state from manager
        this.updateFromCartInitial();
        
        // NEW: Intercept form submission for optimistic updates
        this.setupOptimisticUpdates();
      }
      
      disconnectedCallback() {
        // Clear any pending sync timer
        if (this.syncTimer) {
          clearTimeout(this.syncTimer);
          this.syncTimer = null;
        }

        // Clear optimistic timer
        if (this.optimisticTimer) {
          clearTimeout(this.optimisticTimer);
          this.optimisticTimer = null;
        }

        // Remove event listeners if using delegation
        if (this.bulkEl) {
          this.bulkEl.removeEventListener('click', this.handleBulkClick);
          this.bulkEl.removeEventListener('input', this.handleBulkInput);
        }
        
        if (this.unsubscribe) {
          this.unsubscribe();
        }
      }
      
      setupOptimisticUpdates() {
        // Find the product form in the standard face
        const productForm = this.standardEl?.querySelector('product-form');
        if (!productForm) return;
        
        const form = productForm.querySelector('form');
        if (!form) return;
        
        // Intercept form submission
        form.addEventListener('submit', (e) => {
          // Check if button is disabled (sold out or already submitting)
          const submitButton = form.querySelector('[type="submit"]');
          if (submitButton?.hasAttribute('disabled') || 
              submitButton?.getAttribute('aria-disabled') === 'true') {
            return;
          }
          
          // Apply optimistic updates immediately
          this.applyOptimisticUpdate();
        }, true); // Use capture phase to run before product-form.js handler
        this.setupBulkOptimisticUpdates();
      }

      setupBulkOptimisticUpdates() {
        // Use event delegation on the bulk element itself
        // This way listeners persist even if inner elements are re-rendered
        if (!this.bulkEl) return;
        
        // Remove any existing listeners to prevent duplicates
        this.bulkEl.removeEventListener('click', this.handleBulkClick);
        this.bulkEl.removeEventListener('input', this.handleBulkInput);
        
        // Create bound handlers that we can reference for removal
        this.handleBulkClick = (e) => {
          // Check if a quantity button was clicked
          const button = e.target.closest('.quantity__button');
          if (!button) return;
          
          // Use requestAnimationFrame for reliable timing
          requestAnimationFrame(() => {
            const input = this.bulkEl.querySelector('.quantity__input');
            if (!input) return;
            
            const newQuantity = parseInt(input.value) || 0;
            
            // Optimistically update feedback immediately
            this.updateFeedback(newQuantity > 0, newQuantity);
            
            // Clear any sync timer to prevent interference
            if (this.syncTimer) {
              clearTimeout(this.syncTimer);
              this.syncTimer = null;
            }
            
            // Clear any existing optimistic timer
            clearTimeout(this.optimisticTimer);
            
            // Mark as optimistic and keep it longer for sequential clicks
            this.setAttribute('data-optimistic', 'true');
            this.optimisticTimer = setTimeout(() => {
              this.removeAttribute('data-optimistic');
            }, 800);
          });
        };
        
        this.handleBulkInput = (e) => {
          // Check if the quantity input was changed
          if (!e.target.classList.contains('quantity__input')) return;
          
          const newQuantity = parseInt(e.target.value) || 0;
          
          // Update feedback immediately
          this.updateFeedback(newQuantity > 0, newQuantity);
          
          // Clear sync timer
          if (this.syncTimer) {
            clearTimeout(this.syncTimer);
            this.syncTimer = null;
          }
          
          // Set optimistic flag
          this.setAttribute('data-optimistic', 'true');
          clearTimeout(this.optimisticTimer);
          this.optimisticTimer = setTimeout(() => {
            this.removeAttribute('data-optimistic');
          }, 800);
        };
        
        // Add event listeners using delegation
        this.bulkEl.addEventListener('click', this.handleBulkClick);
        this.bulkEl.addEventListener('input', this.handleBulkInput);
      }
      
      applyOptimisticUpdate() {
        // 1. Create a fake cart item object for the optimistic update
        const optimisticCartItem = { 
          quantity: 1,
          product_id: this.productId,
          variant_id: this.variantId
        };
        
        // 2. Immediately switch to bulk mode
        this.toggle(true, optimisticCartItem);
        
        // 3. DIRECTLY set the input value to 1 (bypass sync logic for initial add)
        const quantityInput = this.bulkEl?.querySelector('.quantity__input');
        if (quantityInput && quantityInput.value === '0') {
          quantityInput.value = '1';
          quantityInput.setAttribute('value', '1');
          quantityInput.setAttribute('data-cart-quantity', '1');
        }
        
        // 4. Update cart bubble optimistically (+1 item)
        window.__hybridCartManager.updateCartBubbleOptimistically(1);
        
        // 5. Mark as optimistic to prevent loading spinners
        this.setAttribute('data-optimistic', 'true');
        
        // Remove flag after a moment (allows real update to take over)
        setTimeout(() => {
          this.removeAttribute('data-optimistic');
        }, 100);
      }
      
      async updateFromCartInitial() {
        try {
          // Use manager's shared fetch
          const cart = await window.__hybridCartManager.getInitialCart();
          this.updateFromCart(cart);
        } catch (e) {
          // Fallback: leave server-rendered state
          console.warn('Failed to fetch initial cart state:', e);
        }
      }
      
      updateFromCart(cart) {
        if (this.hasAttribute('data-optimistic')) return;
        const items = Array.isArray(cart?.items) ? cart.items : [];
        const cartItem = items.find((item) => item.product_id === this.productId);
        const inCart = !!cartItem;
        
        // Toggle visibility AND update quantity
        this.toggle(inCart, cartItem);
        this.updateFeedback(inCart, cartItem?.quantity || 0);
      }
      
      updateFeedback(inCart, quantity = 0) {
        const feedbackEl = document.querySelector(`.hybrid-feedback[data-product-id="${this.productId}"]`);
        if (!feedbackEl) return;
        
        const rewardsEl = feedbackEl.querySelector('.hybrid-feedback__rewards');
        const statusEl = feedbackEl.querySelector('.hybrid-feedback__status');
        const statusTextEl = feedbackEl.querySelector('.hybrid-feedback__status-text');
        
        if (inCart && quantity > 0) {
          // Show cart status
          rewardsEl?.classList.add('hidden');
          statusEl?.classList.remove('hidden');
          if (statusTextEl) {
            statusTextEl.textContent = `${quantity} in cart`;
          }
          // Update ARIA label for accessibility
          if (statusEl) {
            statusEl.setAttribute('aria-label', `View cart - ${quantity} items in cart`);
          }
        } else {
          // Show rewards message
          rewardsEl?.classList.remove('hidden');
          statusEl?.classList.add('hidden');
        }
      }
      
      toggle(inCart, cartItem = null) {
        if (inCart) {
          // Switch to bulk mode
          this.standardEl?.classList.add('hidden');
          this.bulkEl?.classList.remove('hidden');
          
          // CRITICAL: Update the quantity input with actual cart quantity
          if (cartItem) {
            this.updateQuantityInput(cartItem.quantity);
          }
        } else {
          // Switch to standard mode
          this.bulkEl?.classList.add('hidden');
          this.standardEl?.classList.remove('hidden');
        }
        this.updateFeedback(inCart, cartItem?.quantity || 0);
      }
      
      updateQuantityInput(quantity) {
        const quantityInput = this.bulkEl?.querySelector('.quantity__input');
        
        if (!quantityInput || quantity === undefined) return;
        
        const currentDOMValue = parseInt(quantityInput.value);
        const incomingCartQty = parseInt(quantity);
        
        // Clear any existing sync timer
        if (this.syncTimer) {
          clearTimeout(this.syncTimer);
          this.syncTimer = null;
        }
        
        // Check if values don't match (user has pending update)
        if (currentDOMValue !== incomingCartQty) {
          // User's input is ahead of this cart state
          // Set a timer to force sync after a delay if still mismatched
          this.syncTimer = setTimeout(() => {
            const latestDOMValue = parseInt(quantityInput.value);
            const latestCartQty = parseInt(quantityInput.getAttribute('data-cart-quantity') || 0);
            
            // If still out of sync after delay, force update to cart state
            if (latestDOMValue !== incomingCartQty) {
              console.warn('Force syncing quantity input to cart state:', incomingCartQty);
              quantityInput.value = incomingCartQty;
              quantityInput.setAttribute('data-cart-quantity', incomingCartQty);
              quantityInput.setAttribute('value', incomingCartQty);
              
              // Validate after forced sync
              const quantityComponent = this.bulkEl?.querySelector('quantity-input');
              if (quantityComponent?.validateQtyRules) {
                quantityComponent.validateQtyRules();
              }
            }
          }, 1500); // 1.5 second delay before forced sync
          
          return; // Skip immediate update
        }
        
        // Values match - update attributes only (no visual change)
        quantityInput.setAttribute('data-cart-quantity', quantity);
        quantityInput.setAttribute('value', quantity);
        
        // Validate quantity rules if needed
        const quantityComponent = this.bulkEl?.querySelector('quantity-input');
        if (quantityComponent?.validateQtyRules) {
          quantityComponent.validateQtyRules();
        }
      }
    }
  );
}

// Hybrid Feedback Tooltip Handler
document.addEventListener('DOMContentLoaded', () => {
  const overlay = document.querySelector('.hybrid-feedback__overlay');
  
  // Handle rewards button clicks (entire area is now clickable)
  document.addEventListener('click', (e) => {
    const rewardsBtn = e.target.closest('.hybrid-feedback__rewards');
    if (!rewardsBtn) return;
    
    e.preventDefault();
    e.stopPropagation();

    const anyTooltipOpen = document.querySelector('.hybrid-feedback__rewards[aria-expanded="true"]');

    // If a tooltip is open and user clicked a different rewards button, just close the open one
    if (anyTooltipOpen && anyTooltipOpen !== rewardsBtn) {
      anyTooltipOpen.setAttribute('aria-expanded', 'false');
      anyTooltipOpen.nextElementSibling?.setAttribute('hidden', '');
      anyTooltipOpen.closest('.card-wrapper')?.classList.remove('tooltip-open');
      overlay?.classList.remove('active'); // Hide overlay
      return; // Don't open the new one
    }
    
    const tooltip = rewardsBtn.nextElementSibling;
    const isOpen = rewardsBtn.getAttribute('aria-expanded') === 'true';
    
    // Close all other tooltips
    document.querySelectorAll('.hybrid-feedback__rewards[aria-expanded="true"]').forEach(btn => {
      if (btn !== rewardsBtn) {
        btn.setAttribute('aria-expanded', 'false');
        btn.nextElementSibling?.setAttribute('hidden', '');
        btn.closest('.card-wrapper')?.classList.remove('tooltip-open');
      }
    });
    
    // Toggle this tooltip and overlay
    rewardsBtn.setAttribute('aria-expanded', !isOpen);
    if (isOpen) {
      tooltip?.setAttribute('hidden', '');
      rewardsBtn.closest('.card-wrapper')?.classList.remove('tooltip-open');
      overlay?.classList.remove('active'); // Hide overlay
    } else {
      tooltip?.removeAttribute('hidden');
      rewardsBtn.closest('.card-wrapper')?.classList.add('tooltip-open');
      overlay?.classList.add('active'); // Show overlay
    }
  });
  
  // Close tooltip when clicking the overlay
  overlay?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    document.querySelectorAll('.hybrid-feedback__rewards[aria-expanded="true"]').forEach(btn => {
      btn.setAttribute('aria-expanded', 'false');
      btn.nextElementSibling?.setAttribute('hidden', '');
      btn.closest('.card-wrapper')?.classList.remove('tooltip-open');
    });
    
    overlay.classList.remove('active');
  });
  
  // Close tooltip on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('.hybrid-feedback__rewards[aria-expanded="true"]').forEach(btn => {
        btn.setAttribute('aria-expanded', 'false');
        btn.nextElementSibling?.setAttribute('hidden', '');
      });

      // Remove the elevation class from all cards
      document.querySelectorAll('.card-wrapper.tooltip-open').forEach(card => {
        card.classList.remove('tooltip-open');
      });
      
      overlay?.classList.remove('active'); // Hide overlay
    }
  });
  
  // NEW: Handle cart status click to open drawer
  document.addEventListener('click', (e) => {
    const statusEl = e.target.closest('.hybrid-feedback__status');
    if (!statusEl) return;
    
    e.preventDefault();
    
    // Open the cart drawer using the same method as the header cart icon
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.open) {
      cartDrawer.open();
    } else {
      // Fallback: try to trigger the header cart icon click
      const headerCartIcon = document.querySelector('.header__icon--cart');
      if (headerCartIcon) {
        headerCartIcon.click();
      }
    }
  });
  
  // NEW: Handle keyboard activation (Enter/Space) for cart status
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      const statusEl = e.target.closest('.hybrid-feedback__status');
      if (!statusEl) return;
      
      e.preventDefault();
      statusEl.click();
    }
  });
});